import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  CheckCircle2,
  Calendar,
  CreditCard,
  FileText,
  Search,
  Sparkles,
  RefreshCw,
  Layers,
  Clock,
  Users,
  Shield,
  ExternalLink,
} from 'lucide-react';
import { COURSE_PERIODS, COURSE_CATEGORIES, COURSES_DATA } from '../data/coursesData';
import { ref, set, get, remove } from 'firebase/database';
import { database } from '../firebase';
import { normalizeCourse } from '../utils/courseStructure';
import { enrichCourseWithResumen } from '../services/academyFirestore';
import { AdminCursoContenido } from './admin/AdminCursoContenido';

// ─── Tokens de diseño alineados con el Admin Panel ───────────────────────────
const S = {
  card: {
    background: 'rgba(10, 25, 41, 0.6)',
    border: '1px solid rgba(197,160,89,0.18)',
    borderRadius: '14px',
    overflow: 'hidden',
  },
  cardHover: {
    border: '1px solid rgba(197,160,89,0.4)',
  },
  label: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: '#c5a059',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: '4px',
    display: 'block',
  },
  input: {
    width: '100%',
    background: 'rgba(7, 21, 38, 0.8)',
    border: '1px solid rgba(197,160,89,0.25)',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '0.8rem',
    color: '#e2e8f0',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: "'Inter', sans-serif",
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #c5a059 0%, #8a6a2e 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '9px 18px',
    fontSize: '0.78rem',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    transition: 'opacity 0.2s',
    whiteSpace: 'nowrap',
  },
  btnSecondary: {
    background: 'rgba(197,160,89,0.1)',
    color: '#c5a059',
    border: '1px solid rgba(197,160,89,0.3)',
    borderRadius: '10px',
    padding: '9px 18px',
    fontSize: '0.78rem',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    transition: 'opacity 0.2s',
    whiteSpace: 'nowrap',
  },
  btnDanger: {
    background: 'rgba(239,68,68,0.1)',
    color: '#f87171',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '10px',
    padding: '9px 12px',
    fontSize: '0.78rem',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'opacity 0.2s',
  },
  badge: (color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 9px',
    borderRadius: '20px',
    fontSize: '0.65rem',
    fontWeight: 700,
    background: `${color}22`,
    color: color,
    border: `1px solid ${color}44`,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  }),
};

export default function AdminCursosManagement() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState(COURSES_DATA);
  const [activeTab, setActiveTab] = useState('courses');
  const [selectedCourseForLessons, setSelectedCourseForLessons] = useState(COURSES_DATA[0]?.id || '');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal Curso
  const [isCourseModalOpen, setIsCourseModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseForm, setCourseForm] = useState({
    id: '', title: '', description: '', category: 'Pensamiento Matemático',
    periodId: 'p1', periodName: 'Septiembre - Noviembre',
    price: 449, billingCycle: 'Bimestral',
    stripeUrl: 'https://buy.stripe.com/4gMaEX9qg0u63Jo5jb73G08',
    thumbnail: '', total_lessons: 0, duration_minutes: 120, is_published: true,
    temas: []
  });

  const loadCoursesFromFirebase = async () => {
    try {
      const snapshot = await get(ref(database, 'vortex_web_courses'));
      if (snapshot.exists()) {
        const val = snapshot.val();
        const rawList = Object.keys(val).map(k => ({ id: k, ...val[k] })).map(normalizeCourse);
        if (rawList.length > 0) {
          // Enriquece con el resumen real (Firestore vortexAcademyAreas) para
          // mostrar los conteos correctos en el panel admin.
          const enriched = await Promise.all(rawList.map(enrichCourseWithResumen));
          setCourses(enriched);
        }
      }
    } catch (err) {
      console.error('[AdminCursos] Error:', err);
    }
  };

  useEffect(() => { loadCoursesFromFirebase(); }, []);

  const handleSaveCourse = async (e) => {
    e.preventDefault();
    try {
      const id = courseForm.id || `curso_${Date.now()}`;
      const data = normalizeCourse({ ...courseForm, id, updatedAt: Date.now() });
      await set(ref(database, `vortex_web_courses/${id}`), data);
      const updated = courses.map(c => c.id === id ? data : c);
      if (!courses.some(c => c.id === id)) updated.push(data);
      setCourses(updated);
      setIsCourseModalOpen(false);
    } catch (err) { console.error(err); }
  };

  const handleNewCourse = () => {
    setEditingCourse(null);
    setCourseForm({
      id: `curso_nuevo_${Date.now()}`, title: '', description: '',
      category: 'Pensamiento Matemático', periodId: 'p1',
      periodName: 'Septiembre - Noviembre', price: 449, billingCycle: 'Bimestral',
      stripeUrl: 'https://buy.stripe.com/4gMaEX9qg0u63Jo5jb73G08',
      thumbnail: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80',
      total_lessons: 1, duration_minutes: 120, is_published: true,
      temas: []
    });
    setIsCourseModalOpen(true);
  };

  const handleEditCourse = (course) => {
    setEditingCourse(course);
    setCourseForm(normalizeCourse({ ...course }));
    setIsCourseModalOpen(true);
  };

  const handleDeleteCourse = async (id) => {
    if (!window.confirm('¿Eliminar este curso?')) return;
    try {
      await remove(ref(database, `vortex_web_courses/${id}`));
      setCourses(courses.filter(c => c.id !== id));
    } catch (err) { console.error(err); }
  };

  const filteredCourses = courses.filter(c =>
    !searchQuery || c.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedCourseData = courses.find(c => c.id === selectedCourseForLessons);

  const CATEGORY_COLORS = {
    'Pensamiento Matemático': '#38bdf8',
    'Comprensión Lectora': '#c084fc',
    'Redacción Indirecta': '#34d399',
    'Inglés': '#f59e0b',
  };

  const TABS = [
    { id: 'courses', label: 'Gestión de Cursos', icon: <BookOpen size={15} />, count: courses.length },
    { id: 'lessons', label: 'Temas y Contenido', icon: <Layers size={15} /> },
    { id: 'periods', label: 'Periodos de Estudio', icon: <Calendar size={15} /> },
  ];

  return (
    <div style={{ minHeight: '100%', color: '#e2e8f0', fontFamily: "'Inter', sans-serif", padding: '0' }}>

      {/* ── HEADER ── */}
      <div style={{
        padding: '1.5rem 1.75rem 1.25rem',
        borderBottom: '1px solid rgba(197,160,89,0.2)',
        background: 'rgba(7,21,38,0.5)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '1rem',
      }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', ...S.badge('#c5a059'), marginBottom: '8px' }}>
            <Sparkles size={11} />
            <span>Panel de Administración Cursos Vortex</span>
          </div>
          <h1 style={{ margin: 0, fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
            Gestor de <span style={{ color: '#c5a059' }}>Cursos, Lecciones</span> y Periodos
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
            Administra contenidos de capacitación naval · Licencias Stripe · Periodos anuales
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={loadCoursesFromFirebase}           className="adm-btn adm-btn--secondary">
            <RefreshCw size={14} />
            <span>Sincronizar DB</span>
          </button>
          <button onClick={handleNewCourse}           className="adm-btn adm-btn--primary">
            <Plus size={14} />
            <span>Crear Nuevo Curso</span>
          </button>
        </div>
      </div>

      {/* ── TABS ── */}
      <div style={{
        display: 'flex',
        gap: '4px',
        padding: '0 1.75rem',
        borderBottom: '1px solid rgba(197,160,89,0.15)',
        background: 'rgba(7,21,38,0.3)',
        overflowX: 'auto',
      }}>
        {TABS.map(tab => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '7px',
                padding: '12px 18px',
                border: 'none',
                borderBottom: active ? '2px solid #c5a059' : '2px solid transparent',
                background: active ? 'rgba(197,160,89,0.08)' : 'transparent',
                color: active ? '#c5a059' : '#94a3b8',
                fontSize: '0.78rem', fontWeight: 700,
                cursor: 'pointer', transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <span style={{
                  padding: '1px 7px', borderRadius: '20px', fontSize: '0.65rem', fontWeight: 800,
                  background: active ? 'rgba(197,160,89,0.2)' : 'rgba(148,163,184,0.15)',
                  color: active ? '#c5a059' : '#94a3b8',
                }}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── CONTENIDO PRINCIPAL ── */}
      <div style={{ padding: '1.5rem 1.75rem' }}>

        {/* ══ TAB: GESTIÓN DE CURSOS ══ */}
        {activeTab === 'courses' && (
          <div>
            {/* Banner acceso admin */}
            <div style={{
              padding: '12px 16px',
              borderRadius: '12px',
              background: 'rgba(52,211,153,0.06)',
              border: '1px solid rgba(52,211,153,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '14px',
              flexWrap: 'wrap',
              marginBottom: '1.25rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Shield size={15} style={{ color: '#34d399', flexShrink: 0 }} />
                <span style={{ fontSize: '0.75rem', color: '#94a3b8', lineHeight: 1.5 }}>
                  Como <strong style={{ color: '#34d399' }}>Administrador</strong> tienes acceso directo a todos los cursos sin necesidad de licencia.
                  Usa el botón <ExternalLink size={11} style={{ display: 'inline', verticalAlign: 'middle', color: '#34d399' }} /> en cada card para entrar al aula virtual.
                </span>
              </div>
              <button
                onClick={() => navigate('/cursos-vortex')}
                style={{
                  ...S.btnSecondary,
                  padding: '7px 16px',
                  borderColor: 'rgba(52,211,153,0.35)',
                  color: '#34d399',
                  background: 'rgba(52,211,153,0.07)',
                  whiteSpace: 'nowrap',
                }}
              >
                <ExternalLink size={13} />
                <span>Ver Catálogo Público</span>
              </button>
            </div>
            {/* Buscador */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '10px 14px',
              background: 'rgba(7,21,38,0.6)',
              border: '1px solid rgba(197,160,89,0.2)',
              borderRadius: '10px',
              marginBottom: '1.25rem',
              maxWidth: '360px',
            }}>
              <Search size={14} style={{ color: '#94a3b8', flexShrink: 0 }} />
              <input
                placeholder="Buscar curso..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ background: 'none', border: 'none', outline: 'none', color: '#e2e8f0', fontSize: '0.8rem', width: '100%' }}
              />
            </div>

            {/* Grid de Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1.25rem',
            }}>
              {filteredCourses.map(course => {
                const catColor = CATEGORY_COLORS[course.category] || '#c5a059';
                return (
                  <div key={course.id} style={{ ...S.card, display: 'flex', flexDirection: 'column' }}>
                    {/* Thumbnail */}
                    <div style={{ position: 'relative', height: '160px', overflow: 'hidden', flexShrink: 0 }}>
                      {course.thumbnail ? (
                        <img
                          src={course.thumbnail}
                          alt={course.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <div style={{ width: '100%', height: '100%', background: 'rgba(197,160,89,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <BookOpen size={40} style={{ color: '#c5a059', opacity: 0.4 }} />
                        </div>
                      )}
                      {/* Overlay gradient */}
                      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(7,21,38,0.9) 0%, transparent 55%)' }} />
                      {/* Badges */}
                      <span style={{ position: 'absolute', top: '10px', left: '10px', ...S.badge(catColor), backdropFilter: 'blur(6px)', background: 'rgba(7,21,38,0.75)' }}>
                        {course.category}
                      </span>
                      <span style={{ position: 'absolute', top: '10px', right: '10px', ...S.badge('#c5a059'), backdropFilter: 'blur(6px)', background: 'rgba(7,21,38,0.75)' }}>
                        desde $250 MXN
                      </span>
                    </div>

                    {/* Info */}
                    <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <h3 style={{ margin: 0, fontSize: '0.88rem', fontWeight: 700, color: '#fff', lineHeight: 1.3,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {course.title}
                      </h3>
                      <p style={{ margin: 0, fontSize: '0.73rem', color: '#94a3b8', lineHeight: 1.5,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {course.description}
                      </p>

                      {/* Meta row */}
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        paddingTop: '8px', borderTop: '1px solid rgba(197,160,89,0.12)',
                        fontSize: '0.7rem', color: '#64748b',
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Calendar size={11} />
                          {course.periodName || 'Sep – Nov'}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Layers size={11} />
                          {course.resumen ? `${course.resumen.temas} temas · ${course.resumen.lecciones} lecciones` : (course.total_lessons || course.lessons?.length || 0)}
                        </span>
                        <span style={{
                          display: 'flex', alignItems: 'center', gap: '4px',
                          color: course.is_published !== false ? '#34d399' : '#f87171',
                        }}>
                          <Shield size={11} />
                          {course.is_published !== false ? 'Publicado' : 'Borrador'}
                        </span>
                      </div>
                    </div>

                    {/* Acciones */}
                    <div style={{
                      display: 'flex', gap: '8px', padding: '12px 16px',
                      borderTop: '1px solid rgba(197,160,89,0.1)',
                      background: 'rgba(7,21,38,0.4)',
                    }}>
                      <button
                        onClick={() => handleEditCourse(course)}
                        style={{ ...S.btnPrimary, flex: 1, justifyContent: 'center', padding: '8px 12px' }}
                      >
                        <Edit3 size={13} />
                        <span>Editar</span>
                      </button>
                      {/* ── Pase directo admin → aula virtual ── */}
                      <button
                        title="Ver curso como alumno (acceso admin)"
                        onClick={() => navigate(`/cursos-vortex?courseId=${course.id}`)}
                        style={{
                          ...S.btnSecondary,
                          padding: '8px 12px',
                          borderColor: 'rgba(52,211,153,0.35)',
                          color: '#34d399',
                          background: 'rgba(52,211,153,0.07)',
                        }}
                      >
                        <ExternalLink size={13} />
                      </button>
                      <button
                        onClick={() => handleDeleteCourse(course.id)}
                        style={{ ...S.btnDanger, padding: '8px 12px' }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Card "Crear Nuevo" */}
              <div
                onClick={handleNewCourse}
                style={{
                  ...S.card,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  minHeight: '280px', cursor: 'pointer',
                  border: '2px dashed rgba(197,160,89,0.3)',
                  background: 'rgba(197,160,89,0.04)',
                  gap: '12px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.border = '2px dashed rgba(197,160,89,0.7)'}
                onMouseLeave={e => e.currentTarget.style.border = '2px dashed rgba(197,160,89,0.3)'}
              >
                <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'rgba(197,160,89,0.1)', border: '1px solid rgba(197,160,89,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Plus size={22} style={{ color: '#c5a059' }} />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 700, color: '#c5a059' }}>Crear Nuevo Curso</p>
                  <p style={{ margin: '4px 0 0', fontSize: '0.7rem', color: '#64748b' }}>Agregar contenido al catálogo</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══ TAB: TEMAS Y CONTENIDO ══ */}
        {activeTab === 'lessons' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Selector de curso */}
            <div style={{
              ...S.card,
              padding: '16px 18px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              gap: '14px',
            }}>
              <div style={{ flex: 1, minWidth: '220px' }}>
                <label           className="adm-section-title">Curso a editar</label>
                <select
                  value={selectedCourseForLessons}
                  onChange={e => setSelectedCourseForLessons(e.target.value)}
                  style={{ ...S.input, cursor: 'pointer' }}
                >
                  {courses.map(c => (
                    <option key={c.id} value={c.id}>{c.title} ({c.category})</option>
                  ))}
                </select>
              </div>
            </div>

            {selectedCourseData ? (
              <AdminCursoContenido
                key={selectedCourseData.id}
                course={selectedCourseData}
                onSaved={(updated) => {
                  setCourses(courses.map(c => c.id === updated.id ? normalizeCourse(updated) : c));
                }}
              />
            ) : (
              <div style={{ ...S.card, padding: '3rem', textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
                  Selecciona un curso para administrar sus temas, lecciones y exámenes.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ══ TAB: PERIODOS DE ESTUDIO ══ */}
        {activeTab === 'periods' && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '1rem',
          }}>
            {COURSE_PERIODS.map(p => (
              <div key={p.id} style={{ ...S.card, padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={S.badge('#c5a059')}>{p.shortName}</span>
                  {p.active && <span style={S.badge('#34d399')}>🔔 Activo</span>}
                </div>
                <h3 style={{ margin: 0, fontSize: '0.88rem', fontWeight: 700, color: '#fff' }}>{p.name}</h3>
                <p style={{ margin: 0, fontSize: '0.73rem', color: '#94a3b8', lineHeight: 1.55 }}>{p.description}</p>
                <div style={{ paddingTop: '8px', borderTop: '1px solid rgba(197,160,89,0.12)', fontSize: '0.72rem', color: '#c5a059', fontWeight: 700 }}>
                  {p.badge}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ══ MODAL: CREAR / EDITAR CURSO ══ */}
      {isCourseModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
          background: 'rgba(4,12,22,0.85)', backdropFilter: 'blur(10px)',
        }}>
          <form
            onSubmit={handleSaveCourse}
            style={{
              background: '#0a1929', border: '1px solid rgba(197,160,89,0.3)', borderRadius: '18px',
              width: '100%', maxWidth: '660px', maxHeight: '90vh', overflowY: 'auto',
              display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Header modal */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '18px 22px', borderBottom: '1px solid rgba(197,160,89,0.15)',
              position: 'sticky', top: 0, background: '#0a1929', zIndex: 1,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'rgba(197,160,89,0.12)', border: '1px solid rgba(197,160,89,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BookOpen size={16} style={{ color: '#c5a059' }} />
                </div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#fff' }}>
                  {editingCourse ? 'Editar Curso' : 'Crear Nuevo Curso'}
                </h3>
              </div>
              <button type="button" onClick={() => setIsCourseModalOpen(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            {/* Body modal */}
            <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                <div>
                  <label           className="adm-section-title">Título del Curso</label>
                  <input required type="text" value={courseForm.title}
                    onChange={e => setCourseForm({ ...courseForm, title: e.target.value })}
                    className="adm-input" placeholder="Ej: Comprensión Lectora 2026" />
                </div>
                <div>
                  <label           className="adm-section-title">Categoría</label>
                  <select value={courseForm.category}
                    onChange={e => setCourseForm({ ...courseForm, category: e.target.value })}
                    style={{ ...S.input, cursor: 'pointer' }}>
                    <option value="Pensamiento Matemático">Pensamiento Matemático</option>
                    <option value="Comprensión Lectora">Comprensión Lectora</option>
                    <option value="Redacción Indirecta">Redacción Indirecta</option>
                    <option value="Inglés">Inglés</option>
                  </select>
                </div>
                <div>
                  <label           className="adm-section-title">Periodo Asignado</label>
                  <select value={courseForm.periodId}
                    onChange={e => {
                      const sel = COURSE_PERIODS.find(p => p.id === e.target.value);
                      setCourseForm({ ...courseForm, periodId: e.target.value, periodName: sel?.name || '' });
                    }}
                    style={{ ...S.input, cursor: 'pointer' }}>
                    {COURSE_PERIODS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label           className="adm-section-title">Duración (minutos)</label>
                  <input type="number" value={courseForm.duration_minutes}
                    onChange={e => setCourseForm({ ...courseForm, duration_minutes: parseInt(e.target.value) })}
                    className="adm-input" />
                </div>
                <div>
                  <label           className="adm-section-title">Jerarquía (Vortex Academy)</label>
                  <input type="text" value={courseForm.jerarquia || ''}
                    onChange={e => setCourseForm({ ...courseForm, jerarquia: e.target.value })}
                    className="adm-input" placeholder="Ej: TENIENTE DE NAVÍO (vacío = todos)" />
                </div>
                <div>
                  <label           className="adm-section-title">Cuerpo / Servicio</label>
                  <input type="text" value={courseForm.cuerpo || ''}
                    onChange={e => setCourseForm({ ...courseForm, cuerpo: e.target.value })}
                    className="adm-input" placeholder="Ej: Infantería de Marina (vacío = todos)" />
                </div>
                <div>
                  <label           className="adm-section-title">Especialidad</label>
                  <input type="text" value={courseForm.especialidad || ''}
                    onChange={e => setCourseForm({ ...courseForm, especialidad: e.target.value })}
                    className="adm-input" placeholder="Ej: Fusilero (vacío = todas)" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label           className="adm-section-title">URL Imagen Miniatura</label>
                  <input type="url" value={courseForm.thumbnail}
                    onChange={e => setCourseForm({ ...courseForm, thumbnail: e.target.value })}
                    className="adm-input" placeholder="https://images.unsplash.com/..." />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label           className="adm-section-title">Descripción del Curso</label>
                  <textarea rows={3} value={courseForm.description}
                    onChange={e => setCourseForm({ ...courseForm, description: e.target.value })}
                    style={{ ...S.input, resize: 'vertical', fontFamily: "'Inter', sans-serif" }}
                    placeholder="Descripción detallada del contenido del curso..." />
                </div>
              </div>

              {/* Nota sobre precios */}
              <div style={{
                padding: '12px 16px', borderRadius: '10px',
                background: 'rgba(197,160,89,0.07)', border: '1px solid rgba(197,160,89,0.25)',
                fontSize: '0.74rem', color: '#94a3b8', lineHeight: 1.6,
                display: 'flex', alignItems: 'flex-start', gap: '10px',
              }}>
                <CreditCard size={14} style={{ color: '#c5a059', flexShrink: 0, marginTop: '1px' }} />
                <span>
                  El precio se maneja a través de los <strong style={{ color: '#c5a059' }}>planes de licencia Vortex</strong> ($250, $450, $1,100 y $1,900 MXN).
                  Los links de Stripe ya están configurados globalmente — no necesitas crear enlaces individuales por curso.
                </span>
              </div>

              {/* Publicado toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label  style={{  ...S.label,  margin: 0 }} className="adm-label">Estado de Publicación:</label>
                <button
                  type="button"
                  onClick={() => setCourseForm({ ...courseForm, is_published: !courseForm.is_published })}
                  style={{
                    padding: '6px 14px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', border: 'none',
                    background: courseForm.is_published ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.12)',
                    color: courseForm.is_published ? '#34d399' : '#f87171',
                  }}
                >
                  {courseForm.is_published ? '✅ Publicado' : '⛔ Borrador'}
                </button>
              </div>
            </div>

            {/* Footer modal */}
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: '10px',
              padding: '16px 22px', borderTop: '1px solid rgba(197,160,89,0.15)',
              position: 'sticky', bottom: 0, background: '#0a1929',
            }}>
              <button type="button" onClick={() => setIsCourseModalOpen(false)}           className="adm-btn adm-btn--secondary">Cancelar</button>
              <button type="submit"           className="adm-btn adm-btn--primary">
                <Save size={14} />
                <span>Guardar Curso</span>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
